import { Component } from 'react';
import Link from 'next/link';

import { ProductReview } from 'types';
import NextSteps from 'components/NextSteps';
import SalesCalloutHTO from 'components/SalesCalloutHTO';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import Layout2x1ImageRight from 'components/Layout2x1ImageRight';
import Layout2x1ImageLeft from 'components/Layout2x1ImageLeft';
import Picture from 'components/Picture';
import Quote from 'components/Quote';
import StarRating from 'components/StarRating';
import Loading from 'components/Loading';
import Reviews from 'components/Reviews';
import AjaxService from 'services/ajax';
import { SiteReviewsQueryResponse } from 'services/productsApi';
import { withPageStaticProps } from 'utils/header';

const ajax = AjaxService();

interface Props {}

interface State {
  loading: boolean;
  reviews: ProductReview[];
  page: number;
  averageStarRating: number;
  totalReviewCount?: number;
  canLoadMore?: boolean;
}

class ReviewsComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      reviews: [],
      loading: true,
      page: 1,
      averageStarRating: 5,
      totalReviewCount: undefined,
    };

    this.loadMoreReviews = this.loadMoreReviews.bind(this);
    this.getSiteReviews = this.getSiteReviews.bind(this);
  }

  async componentDidMount() {
    try {
      const r = await this.getSiteReviews(1);
      const { bottomLine, reviews } = r.siteReviews;

      this.setState({
        reviews,
        loading: false,
        page: 1,
        canLoadMore: reviews.length === 20,
        averageStarRating: bottomLine.averageScore,
        totalReviewCount: bottomLine.totalReview,
      });
    } catch (e) {
      // silently fail and do not show reviews
      this.setState({
        loading: false,
      });
      console.error(e);
    }
  }

  formatReviews(reviews: ProductReview[]) {
    return reviews.map((review) => ({
      ...review,
      content: review.content.replace(/&#x27;/g, "'"),
      createdAt: new Date(review.createdAt).getTime(),
    }));
  }

  async getSiteReviews(page: number) {
    const reviews = await ajax.post<SiteReviewsQueryResponse>('/api/get-all-site-reviews', {
      body: {
        perPage: 20,
        page,
      },
    });

    return reviews;
  }

  async loadMoreReviews() {
    this.setState({ canLoadMore: false });

    const page = this.state.page + 1;
    const r = await this.getSiteReviews(page);
    const { reviews } = r.siteReviews;
    this.setState((state) => ({
      page,
      reviews: [...state.reviews, ...reviews],
      canLoadMore: reviews.length !== 0,
    }));
  }

  render() {
    const { reviews, loading, canLoadMore, averageStarRating, totalReviewCount } = this.state;
    return (
      <>
        <div className="m-section-hero container">
          <h1 className="text-h1">Customer Reviews</h1>
        </div>
        <Layout2x1ImageLeft
          className="m-section"
          image={
            <Picture
              className="aspect-ratio-5by7"
              url="https://gentux.imgix.net/1632770657_reviewimage1.png"
              alt="Bride and groom standing outside."
            />
          }
        >
          <Quote citationName="David">
            I could not be more of a raving fan of Menguin... I highly recommend. Thank you so much to Menguin for helping me pull off the
            exact look I envisioned once my Bride and I decided on our color scheme!
          </Quote>
        </Layout2x1ImageLeft>
        <Layout2x1ImageRight
          className="m-section"
          image={<Picture className="" url="https://gentux.imgix.net/1632770656_reviewimage2.png" alt="Wedding party outside." />}
        >
          <Quote citationName="Owen">
            I was skeptical that they would be able to send me a perfectly sized suit without knowing my measurements and I was amazed… The
            suit fit me like a glove and it felt great to wear during the wedding. All groomsman should get their suits from here.
          </Quote>
        </Layout2x1ImageRight>
        <div className="container space-y-32">
          <div className="space-y-16 sm:flex sm:justify-between sm:space-y-0">
            <div>
              <h2 className="text-h2">Overall Rating</h2>

              <div className="flex items-center gap-8">
                <StarRating rating={averageStarRating} />

                <p className="text-sm text-gray-dark">({totalReviewCount} reviews)</p>
              </div>
            </div>

            <Link
              href="/collection/[category]"
              as="/collection/tuxedos-and-suits"
              className="btn-info tracker-cta-reviews-sidebar-20190104-113749  btn"
            >
              See Collection
            </Link>
          </div>

          {(() => {
            if (loading) {
              return <Loading />;
            } else if (!loading && reviews.length > 0) {
              return <Reviews reviews={this.formatReviews(reviews)} canLoadMore={canLoadMore} loadMoreClicked={this.loadMoreReviews} />;
            } else {
              // If reviews api has loaded and there were no reviews returned
              // or api threw an exception: silently fail and don't show
              // dynamic reviews
              return <></>;
            }
          })()}
        </div>
        <NextSteps
          ctaPageName="reviews"
          salesCallout1={<SalesCalloutHTO ctaPageName="reviews" />}
          salesCallout2={<SalesCalloutSwatches ctaPageName="reviews" />}
        />
      </>
    );
  }
}

export const getStaticProps = withPageStaticProps();

export default ReviewsComponent;
